/**
 * Serializes an entity's already-loaded properties as Turtle, RDF/XML, JSON-LD or N-Triples,
 * client-side.
 */

import type { EntityProperty, EntityObject } from '@/types';
import type { PrefixMap } from '@utils/prefix.utils';

function escapeTurtle(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t');
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Return the most specific prefix match, or angle-bracket form. */
function angleOrPrefixed(uri: string, prefixes: PrefixMap): string {
  let bestPrefix = '';
  let bestNs = '';
  for (const [p, ns] of Object.entries(prefixes)) {
    if (uri.startsWith(ns) && ns.length > bestNs.length) {
      bestPrefix = p;
      bestNs = ns;
    }
  }
  if (bestNs) return `${bestPrefix}:${uri.slice(bestNs.length)}`;
  return `<${uri}>`;
}

function objToTurtle(obj: EntityObject, prefixes: PrefixMap): string {
  if (obj.type === 'uri') return angleOrPrefixed(obj.value, prefixes);
  if (obj.type === 'bnode') return `_:${obj.value}`;
  const escaped = `"${escapeTurtle(obj.value)}"`;
  if (obj.lang) return `${escaped}@${obj.lang}`;
  if (obj.datatype) return `${escaped}^^${angleOrPrefixed(obj.datatype, prefixes)}`;
  return escaped;
}

export function serializeToTurtle(uri: string, properties: EntityProperty[], prefixes: PrefixMap): string {
  // Collect which prefixes are actually needed
  const used = new Set<string>();
  const markUri = (u: string) => {
    let bestNs = '';
    let bestPrefix = '';
    for (const [p, ns] of Object.entries(prefixes)) {
      if (u.startsWith(ns) && ns.length > bestNs.length) { bestPrefix = p; bestNs = ns; }
    }
    if (bestNs) used.add(bestPrefix);
  };
  markUri(uri);
  for (const prop of properties) {
    markUri(prop.predicate);
    for (const obj of prop.objects) {
      if (obj.type === 'uri') markUri(obj.value);
      if (obj.datatype) markUri(obj.datatype);
    }
  }

  const lines: string[] = [];
  for (const p of [...used].sort()) {
    lines.push(`@prefix ${p}: <${prefixes[p]}> .`);
  }
  if (used.size > 0) lines.push('');

  if (properties.length === 0) {
    lines.push(`<${uri}> .`);
    return lines.join('\n');
  }

  lines.push(`<${uri}>`);
  const triples: string[] = [];
  for (const prop of properties) {
    const pred = angleOrPrefixed(prop.predicate, prefixes);
    for (const obj of prop.objects) {
      triples.push(`    ${pred} ${objToTurtle(obj, prefixes)}`);
    }
  }
  for (let i = 0; i < triples.length; i++) {
    lines.push(triples[i] + (i < triples.length - 1 ? ' ;' : ' .'));
  }
  return lines.join('\n');
}

export function serializeToRdfXml(uri: string, properties: EntityProperty[]): string {
  const nsMap: Record<string, string> = {
    rdf: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
  };
  let nsCounter = 0;

  function registerNs(predUri: string): { alias: string; local: string } | null {
    const hashIdx = predUri.lastIndexOf('#');
    const slashIdx = predUri.lastIndexOf('/');
    const splitIdx = Math.max(hashIdx, slashIdx);
    if (splitIdx < 0) return null;
    const ns = predUri.slice(0, splitIdx + 1);
    const local = predUri.slice(splitIdx + 1);
    // local name must be a valid XML name
    if (!local || !/^[a-zA-Z_][\w.-]*$/.test(local)) return null;
    const existing = Object.entries(nsMap).find(([, v]) => v === ns);
    if (existing) return { alias: existing[0], local };
    const alias = `ns${nsCounter++}`;
    nsMap[alias] = ns;
    return { alias, local };
  }

  // Pre-scan so nsMap is complete before building xml attrs
  for (const prop of properties) {
    registerNs(prop.predicate);
    for (const obj of prop.objects) {
      if (obj.datatype) registerNs(obj.datatype);
    }
  }

  const nsAttrs = Object.entries(nsMap)
    .map(([a, u]) => `xmlns:${a}="${escapeXml(u)}"`)
    .join('\n             ');

  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<rdf:RDF ${nsAttrs}>`,
    `  <rdf:Description rdf:about="${escapeXml(uri)}">`,
  ];

  for (const prop of properties) {
    const parsed = registerNs(prop.predicate);
    if (!parsed) continue;
    const { alias, local } = parsed;
    for (const obj of prop.objects) {
      if (obj.type === 'uri') {
        lines.push(`    <${alias}:${local} rdf:resource="${escapeXml(obj.value)}"/>`);
      } else if (obj.type === 'bnode') {
        lines.push(`    <${alias}:${local} rdf:nodeID="${escapeXml(obj.value)}"/>`);
      } else {
        const langAttr = obj.lang ? ` xml:lang="${obj.lang}"` : '';
        const dtAttr = obj.datatype && !obj.lang ? ` rdf:datatype="${escapeXml(obj.datatype)}"` : '';
        lines.push(
          `    <${alias}:${local}${langAttr}${dtAttr}>${escapeXml(obj.value)}</${alias}:${local}>`
        );
      }
    }
  }

  lines.push('  </rdf:Description>');
  lines.push('</rdf:RDF>');
  return lines.join('\n');
}

function objToJsonLdValue(obj: EntityObject): unknown {
  if (obj.type === 'uri') return { '@id': obj.value };
  if (obj.type === 'bnode') return { '@id': `_:${obj.value}` };
  if (obj.lang) return { '@value': obj.value, '@language': obj.lang };
  if (obj.datatype) return { '@value': obj.value, '@type': obj.datatype };
  return { '@value': obj.value };
}

export function serializeToJsonLd(uri: string, properties: EntityProperty[], prefixes: PrefixMap): string {
  // Only include prefixes that match something in this entity's data
  const used: Record<string, string> = {};
  const markUri = (u: string) => {
    let bestNs = '';
    let bestPrefix = '';
    for (const [p, ns] of Object.entries(prefixes)) {
      if (u.startsWith(ns) && ns.length > bestNs.length) { bestPrefix = p; bestNs = ns; }
    }
    if (bestNs) used[bestPrefix] = bestNs;
  };
  for (const prop of properties) {
    markUri(prop.predicate);
    for (const obj of prop.objects) {
      if (obj.type === 'uri') markUri(obj.value);
    }
  }

  const doc: Record<string, unknown> = { '@context': used, '@id': uri };

  for (const prop of properties) {
    const key = angleOrPrefixed(prop.predicate, prefixes).replace(/^<|>$/g, '');
    const values = prop.objects.map(objToJsonLdValue);
    doc[key] = values.length === 1 ? values[0] : values;
  }

  return JSON.stringify(doc, null, 2);
}

export function serializeToNTriples(uri: string, properties: EntityProperty[]): string {
  return properties
    .flatMap((prop) => prop.objects.map((obj) => `<${uri}> <${prop.predicate}> ${objToTurtle(obj, {})} .`))
    .join('\n');
}
